import { useEffect, useState } from 'react'
import { myLanes } from '../lib/support'
import { AppShell } from '../components/AppShell'
import { TermsDocument } from '../components/TermsDocument'
import { supabase } from '../lib/supabase'
import { can, canGallery, type Me } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { useHubLang, dateLocaleFor } from '../lib/i18n'
import { TERMS } from '../lib/hubCopy'
import { initialsOf } from '../lib/initials'
import { loadMyTerms, loadTermsText, type TermsKind, type TermsStatus, type TermsText } from '../lib/terms'

/**
 * #/terms (165). The current terms of use and Star One terms, each with the
 * date the reader agreed. Reached from Terms in the identity menu; the help
 * entries terms_what and star_one_terms send people here.
 *
 * Read-only. Agreeing to the terms of use happened at the gate (nobody gets
 * here without it); joining Star One happens on Star One, so this page links
 * there rather than carrying a second agreement form.
 */
export function Terms({ me }: { me: Me }) {
  const lang = useHubLang()
  const t = TERMS[lang]
  const [status, setStatus] = useState<TermsStatus[] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    loadMyTerms()
      .then((r) => live && setStatus(r))
      .catch(() => live && setFailed(true))
    return () => {
      live = false
    }
  }, [])

  return (
    <AppShell
      current="Terms"
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      {failed ? (
        <p className="alert" role="alert">{t.loadFailed}</p>
      ) : status === null ? (
        <p className="empty" aria-busy="true">{t.loading}</p>
      ) : status.length === 0 ? (
        <p className="empty">{t.nothingToShow}</p>
      ) : (
        status.map((s) => <OneDocument key={s.kind} kind={s.kind} status={s} />)
      )}
    </AppShell>
  )
}

function OneDocument({ kind, status }: { kind: TermsKind; status: TermsStatus }) {
  const lang = useHubLang()
  const t = TERMS[lang]
  const [doc, setDoc] = useState<TermsText | null>(null)
  const [locale, setLocale] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    loadTermsText(kind, locale ?? undefined)
      .then((d) => live && setDoc(d))
      .catch(() => live && setFailed(true))
    return () => {
      live = false
    }
  }, [kind, locale])

  const agreed = status.acceptedAt
    ? new Date(status.acceptedAt).toLocaleDateString(dateLocaleFor(lang), {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : null

  return (
    <section className="card terms__page">
      <div className="card__body">
        {failed ? (
          <p className="alert" role="alert">{t.loadFailed}</p>
        ) : !doc ? (
          <p className="empty" aria-busy="true">{t.loading}</p>
        ) : (
          <TermsDocument doc={doc} onSwitchLocale={setLocale} />
        )}
        <p className="terms__status">
          {agreed ? (
            t.agreedOn(agreed)
          ) : kind === 'star_one' ? (
            <>
              {t.starNotJoined} <a href="#/stars">{t.joinFromStarOne}</a>
            </>
          ) : null}
        </p>
      </div>
    </section>
  )
}
